import { ENDPOINTS } from '../utils/endpoints';
import { getAPIwithToken, postAPIwithToken, putAPIwithToken, deleteAPIwithToken } from '../utils/fetchApi';
import type { Scheme, SchemeFilters, CreateSchemeForm, BackendScheme, DistType } from '../types/scheme';
import { getTerritoriesAPI } from './locations';

let cachedTerritoryMap: Record<string | number, string> | null = null;

export const fetchTerritoryMap = async (): Promise<Record<string | number, string>> => {
  if (cachedTerritoryMap) return cachedTerritoryMap;
  try {
    const res = await getTerritoriesAPI();
    const rawList = res?.data || res?.territories || (Array.isArray(res) ? res : []);
    const tMap: Record<string | number, string> = {};
    if (Array.isArray(rawList)) {
      rawList.forEach((t: any) => {
        if (t.id && t.name) {
          tMap[t.id] = t.name;
          tMap[String(t.id)] = t.name;
        }
      });
    }
    cachedTerritoryMap = tMap;
    return tMap;
  } catch (err) {
    console.warn('Could not fetch territories map for scheme:', err);
    return {};
  }
};

export const mapBackendSchemeToFrontend = (
  item: BackendScheme,
  territoryMap?: Record<string | number, string>
): Scheme => {
  const zoneName = item.zone?.name || '';
  const cityName = item.city?.name || '';
  
  const territoryId =
    item.territory?.id ||
    (item as any).territoryId ||
    (item as any).territory_id ||
    item.city?.territoryId ||
    (item.city as any)?.territory_id;

  const territoryName =
    item.territory?.name ||
    (item as any).territoryName ||
    (item as any).territory?.name ||
    (item.city as any)?.territory?.name ||
    (item.city as any)?.territoryName ||
    (territoryMap && territoryId && territoryMap[territoryId] ? territoryMap[territoryId] : '') ||
    '';

  const areaName = item.area?.name || (item as any).areaName || '';

  const locationParts: string[] = [];
  if (zoneName) locationParts.push(zoneName);
  if (cityName) locationParts.push(cityName);
  if (territoryName) locationParts.push(territoryName);
  if (areaName) locationParts.push(areaName);

  const zoneCityStr = locationParts.length > 0 ? locationParts.join(' / ') : 'All Locations';

  const productName =
    typeof item.product === 'object' && item.product
      ? item.product.name || item.product.displayName || 'Gowardhan Product'
      : typeof item.product === 'string'
      ? item.product
      : (item as any).productName || 'Gowardhan Product';

  const schemeCodeStr = (item as any).code ? String((item as any).code).replace(/^SCH-/i, '') : (item.id ? `${item.id}` : '');

  const sapCodeStr =
    typeof item.product === 'object' && item.product?.sapCode
      ? item.product.sapCode
      : (item as any).sapCode || (item as any).productSapCode || (item as any).product_sap_code || '-';

  const rawDistTypeId =
    item.distributorTypeId ||
    (item as any).distributor_type_id ||
    (typeof item.distributorType === 'object' && item.distributorType ? item.distributorType.id : undefined);

  const distTypeIdNum = rawDistTypeId ? Number(rawDistTypeId) : undefined;

  const distTypeName: DistType =
    typeof item.distributorType === 'object' && item.distributorType?.name
      ? item.distributorType.name
      : typeof item.distributorType === 'string'
      ? item.distributorType
      : item.distributorTypeId === 1 || item.distributorTypeId === 10
      ? 'GT'
      : item.distributorTypeId === 2 || item.distributorTypeId === 20
      ? 'MT'
      : 'All';

  const rawStartDate =
    item.startDate ||
    (item as any).start_date ||
    (item as any).start_Date ||
    (item as any).validityStart ||
    (item as any).validity_start ||
    (item as any).fromDate ||
    (item as any).from_date ||
    '';

  const rawEndDate =
    item.endDate ||
    (item as any).end_date ||
    (item as any).end_Date ||
    (item as any).validityEnd ||
    (item as any).validity_end ||
    (item as any).toDate ||
    (item as any).to_date ||
    '';

  const startDateFormatted = rawStartDate
    ? new Date(rawStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const endDateFormatted = rawEndDate
    ? new Date(rawEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  const validityStr = startDateFormatted && endDateFormatted
    ? `${startDateFormatted} - ${endDateFormatted}`
    : startDateFormatted || endDateFormatted || 'N/A';

  const createdOnStr = item.createdAt
    ? new Date(item.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';

  let status: 'Active' | 'Expired' | 'Upcoming' = 'Active';
  if (String(item.status) === '0' || item.status === 0) {
    status = 'Expired';
  } else if (rawEndDate && new Date(rawEndDate) < new Date()) {
    status = 'Expired';
  } else if (rawStartDate && new Date(rawStartDate) > new Date()) {
    status = 'Upcoming';
  }

  let citiesList: string[] = [];
  if (Array.isArray((item as any).cities) && (item as any).cities.length > 0) {
    citiesList = (item as any).cities
      .map((c: any) => (typeof c === 'string' ? c : c.name || c.cityName || c.city?.name || ''))
      .filter(Boolean);
  } else if (Array.isArray((item as any).citySchemes) && (item as any).citySchemes.length > 0) {
    citiesList = (item as any).citySchemes
      .map((cs: any) => cs.city?.name || cs.cityName || cs.name || '')
      .filter(Boolean);
  } else if (Array.isArray((item as any).schemeCities) && (item as any).schemeCities.length > 0) {
    citiesList = (item as any).schemeCities
      .map((sc: any) => sc.city?.name || sc.cityName || sc.name || '')
      .filter(Boolean);
  } else if (Array.isArray((item as any).citiesList) && (item as any).citiesList.length > 0) {
    citiesList = (item as any).citiesList.filter(Boolean);
  } else if (cityName) {
    citiesList = [cityName];
  } else {
    citiesList = ['All Cities'];
  }

  return {
    id: String(item.id),
    code: schemeCodeStr,
    sapCode: sapCodeStr,
    name: item.name,
    zone: zoneName,
    city: cityName,
    territory: territoryName,
    area: areaName,
    zoneCity: zoneCityStr,
    distType: distTypeName,
    distributorTypeId: distTypeIdNum,
    product: productName,
    productId: item.productId || (typeof item.product === 'object' ? item.product?.id : undefined),
    eligibleProducts: Number(item.purchaseQuantity ?? (item as any).purchase_quantity ?? (item as any).eligibleUnits ?? 0),
    eligibleUnitType: 'Units',
    freeProducts: Number(item.freeQuantity ?? (item as any).free_quantity ?? 0),
    startDate: rawStartDate,
    endDate: rawEndDate,
    validity: validityStr,
    status,
    createdAt: item.createdAt,
    createdBy: typeof item.creator === 'object' && item.creator?.name ? item.creator.name : (item as any).createdByName || (item.createdBy ? `User #${item.createdBy}` : 'Admin'),
    createdOn: createdOnStr,
    duration: validityStr,
    citiesList,
    description: item.description || `Distributors purchasing ${item.purchaseQuantity || 0} units get ${item.freeQuantity || 0} free.`,
    crateMapping: item.crateUnits ? `${item.crateUnits} Units/Crate` : (item as any).crate?.units ? `${(item as any).crate.units} Units/Crate` : '12 Units/Crate',
    rawBackendData: item,
  };
};

export const getSchemes = async (filters?: Partial<SchemeFilters>) => {
  const territoryMap = await fetchTerritoryMap();

  const payload: Record<string, any> = {
    page: filters?.page || 1,
    limit: filters?.limit || 20,
  };

  if (filters?.search) {
    payload.search = filters.search.trim();
  }

  if (filters?.status && filters.status !== 'SKU Unit' && filters.status !== 'Status' && filters.status !== 'all') {
    if (filters.status.toLowerCase() === 'active') payload.status = '1';
    else if (filters.status.toLowerCase() === 'expired') payload.status = '0';
  }

  try {
    const res = await postAPIwithToken<any>(ENDPOINTS.SCHEMES_LIST, payload);
    if (res && (res.status === 'success' || res.success) && Array.isArray(res.data)) {
      const mappedSchemes = res.data.map((item: any) => mapBackendSchemeToFrontend(item, territoryMap));
      const total = res.pagination?.totalCount || res.pagination?.total || mappedSchemes.length;
      return {
        success: true,
        data: mappedSchemes,
        total,
        page: res.pagination?.page || payload.page,
        limit: res.pagination?.limit || payload.limit,
      };
    }
  } catch {
    // Fallback GET /api/v1/erp/schemes
    try {
      const res = await getAPIwithToken<any>(ENDPOINTS.GET_SCHEMES);
      const rawList = res?.data || res;
      if (Array.isArray(rawList)) {
        const mappedSchemes = rawList.map((item: any) => mapBackendSchemeToFrontend(item, territoryMap));
        return {
          success: true,
          data: mappedSchemes,
          total: mappedSchemes.length,
          page: payload.page,
          limit: payload.limit,
        };
      }
    } catch (e) {
      console.warn('Error fetching schemes:', e);
    }
  }

  return {
    success: true,
    data: [],
    total: 0,
    page: payload.page,
    limit: payload.limit,
  };
};

export const getSchemeById = async (id: string | number): Promise<Scheme | null> => {
  try {
    const res = await getAPIwithToken<any>(ENDPOINTS.GET_SCHEME_BY_ID(id));
    const data = res?.data || res;
    if (data && (data.id || data.name)) {
      const territoryMap = await fetchTerritoryMap();
      return mapBackendSchemeToFrontend(data, territoryMap);
    }
  } catch (err) {
    console.warn(`GET /erp/schemes/${id} failed, attempting search fallback:`, err);
  }

  const resList = await getSchemes({ search: String(id), limit: 1 });
  if (resList.data && resList.data.length > 0) {
    return resList.data[0];
  }
  return null;
};

export const createScheme = async (formData: CreateSchemeForm): Promise<Scheme> => {
  const payload: Record<string, any> = {
    name: formData.schemeName,
    description: formData.description || `Buy ${formData.eligibleUnits} Get ${formData.freeProducts} Free`,
    schemeType: formData.schemeType || 'BUY_X_GET_Y',
    purchaseQuantity: Number(formData.eligibleUnits || 0),
    freeQuantity: Number(formData.freeProducts || 0),
    extendable: formData.extendable ?? true,
    priority: formData.priority || 1,
    status: 1,
  };

  if (formData.productId) {
    payload.productId = Number(formData.productId);
  }

  if (formData.crateId) {
    payload.crateId = Number(formData.crateId);
  }

  if (formData.crateUnits) {
    payload.crateUnits = Number(formData.crateUnits);
  }

  if (formData.distributorTypeId) {
    payload.distributorTypeId = Number(formData.distributorTypeId);
  } else if (formData.distributorType) {
    if (typeof formData.distributorType === 'number') {
      payload.distributorTypeId = formData.distributorType;
    } else {
      const parsed = Number(formData.distributorType);
      if (!isNaN(parsed)) {
        payload.distributorTypeId = parsed;
      } else {
        const nameUpper = String(formData.distributorType).toUpperCase();
        if (nameUpper === 'MT') payload.distributorTypeId = 2;
        else if (nameUpper === 'GT') payload.distributorTypeId = 1;
      }
    }
  }

  if (formData.zoneId) {
    payload.zoneId = Number(formData.zoneId);
  }

  let cityIdsArray: number[] = [];
  if (formData.cityIds && Array.isArray(formData.cityIds) && formData.cityIds.length > 0) {
    cityIdsArray = formData.cityIds.map(Number).filter((n) => !isNaN(n));
  } else if (formData.cityId) {
    if (typeof formData.cityId === 'string') {
      cityIdsArray = formData.cityId
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => !isNaN(n));
    } else if (typeof formData.cityId === 'number') {
      cityIdsArray = [formData.cityId];
    }
  }

  if (cityIdsArray.length > 0) {
    payload.cityIds = cityIdsArray;
    payload.cityId = cityIdsArray.length === 1 ? cityIdsArray[0] : cityIdsArray;
  }

  if (formData.startDate) {
    payload.startDate = formData.startDate.includes('T') ? formData.startDate : `${formData.startDate}T00:00:00.000Z`;
  }

  if (formData.endDate) {
    payload.endDate = formData.endDate.includes('T') ? formData.endDate : `${formData.endDate}T23:59:59.999Z`;
  }

  const res = await postAPIwithToken<any>(ENDPOINTS.CREATE_SCHEME, payload);
  const data = res?.data || res;

  if (data && (data.id || data.name)) {
    return mapBackendSchemeToFrontend(data);
  }

  throw new Error(res?.message || 'Failed to create scheme');
};

export const updateScheme = async (idOrFormData: string | number | CreateSchemeForm, formDataParam?: CreateSchemeForm): Promise<Scheme> => {
  const isIdGiven = typeof idOrFormData === 'string' || typeof idOrFormData === 'number';
  const id = isIdGiven ? idOrFormData : (idOrFormData as CreateSchemeForm).id;
  const formData = isIdGiven ? formDataParam! : (idOrFormData as CreateSchemeForm);

  if (!id) {
    return createScheme(formData);
  }

  const payload: Record<string, any> = {};
  if (formData.schemeName) payload.name = formData.schemeName;
  if (formData.description) payload.description = formData.description;
  if (formData.schemeType) payload.schemeType = formData.schemeType;
  if (formData.eligibleUnits !== undefined) payload.purchaseQuantity = Number(formData.eligibleUnits);
  if (formData.freeProducts !== undefined) payload.freeQuantity = Number(formData.freeProducts);
  if (formData.productId) payload.productId = Number(formData.productId);
  if (formData.crateId) payload.crateId = Number(formData.crateId);
  if (formData.crateUnits) payload.crateUnits = Number(formData.crateUnits);
  if (formData.distributorTypeId) payload.distributorTypeId = Number(formData.distributorTypeId);
  else if (formData.distributorType) {
    if (typeof formData.distributorType === 'number') {
      payload.distributorTypeId = formData.distributorType;
    } else {
      const parsed = Number(formData.distributorType);
      if (!isNaN(parsed)) {
        payload.distributorTypeId = parsed;
      } else {
        const nameUpper = String(formData.distributorType).toUpperCase();
        if (nameUpper === 'MT') payload.distributorTypeId = 2;
        else if (nameUpper === 'GT') payload.distributorTypeId = 1;
      }
    }
  }
  if (formData.zoneId) payload.zoneId = Number(formData.zoneId);
  let cityIdsArray: number[] = [];
  if (formData.cityIds && Array.isArray(formData.cityIds) && formData.cityIds.length > 0) {
    cityIdsArray = formData.cityIds.map(Number).filter((n) => !isNaN(n));
  } else if (formData.cityId) {
    if (typeof formData.cityId === 'string') {
      cityIdsArray = formData.cityId
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => !isNaN(n));
    } else if (typeof formData.cityId === 'number') {
      cityIdsArray = [formData.cityId];
    }
  }

  if (cityIdsArray.length > 0) {
    payload.cityIds = cityIdsArray;
    payload.cityId = cityIdsArray.length === 1 ? cityIdsArray[0] : cityIdsArray;
  }
  if (formData.startDate) payload.startDate = formData.startDate.includes('T') ? formData.startDate : `${formData.startDate}T00:00:00.000Z`;
  if (formData.endDate) payload.endDate = formData.endDate.includes('T') ? formData.endDate : `${formData.endDate}T23:59:59.999Z`;
  if (formData.priority !== undefined) payload.priority = formData.priority;
  if (formData.extendable !== undefined) payload.extendable = formData.extendable;

  const res = await putAPIwithToken<any>(ENDPOINTS.UPDATE_SCHEME(id), payload);
  const data = res?.data || res;
  if (data && (data.id || data.name)) {
    return mapBackendSchemeToFrontend(data);
  }
  throw new Error(res?.message || 'Failed to update scheme');
};

export const removeSchemeApi = async (id: number | string): Promise<boolean> => {
  const endpoint = ENDPOINTS.DELETE_SCHEME(id);
  const res = await deleteAPIwithToken<any>(endpoint);
  if (res && (res.status === 'success' || res.success)) {
    return true;
  }
  throw new Error(res?.message || 'Failed to delete scheme');
};

export const extendScheme = async (id: number | string, extendedDays: number, remarks?: string): Promise<Scheme> => {
  const endpoint = ENDPOINTS.EXTEND_SCHEME(id);
  const payload = {
    extendedDays: Number(extendedDays),
    remarks: remarks || `Extended by ${extendedDays} days`,
  };
  const res = await postAPIwithToken<any>(endpoint, payload);
  const data = res?.data || res;
  if (data && (data.id || data.name || data.newEndDate)) {
    return mapBackendSchemeToFrontend(data);
  }
  throw new Error(res?.message || 'Failed to extend scheme');
};

export const batchExtendSchemeValidity = async (ids: string[], days: 15 | 30, remarks?: string) => {
  const results = await Promise.allSettled(
    ids.map((id) => extendScheme(id, days, remarks))
  );

  const fulfilledCount = results.filter((r) => r.status === 'fulfilled').length;
  if (fulfilledCount === 0 && ids.length > 0) {
    const firstReason = (results[0] as PromiseRejectedResult).reason;
    throw new Error(firstReason?.message || firstReason || 'Failed to extend validity for selected schemes');
  }

  return { success: true, count: fulfilledCount, days };
};


